import { useState, useEffect, useCallback } from "react";

import { getStationsApi } from "../../../../services/stations";
import { getCustomerNames } from "../../../../services/clearing/customers";
import { Customer, Station } from "../interfaces";

export const useStationData = ({ toast }) => {
  const [stations, setStations] = useState<Station[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStations = useCallback(async () => {
    setLoading(true);
    try {
      const [stationsData, customers] = await Promise.all([
        getStationsApi("COCHARGE"),
        getCustomerNames(),
      ]);

      const customerMap =
        customers &&
        new Map(
          customers?.map((customer: Customer) => [
            customer.stationId,
            customer.customerName,
          ]),
        );

      const formattedStations = stationsData
        ? stationsData?.map((station: Station) => ({
            ...station,
            status: station.evses?.[0]?.status || "UNKNOWN",
            customerName: customerMap.get(station.id) || "-",
            isReservable:
              station.evses?.some((evse) =>
                evse.capabilities?.includes("RESERVABLE"),
              ) ?? false,
          }))
        : [];

      setStations(formattedStations);
      setError(null);
    } catch (error) {
      setError("Something went wrong, please try again later.");
      toast?.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch stations",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [toast, setLoading, setError, setStations]);

  useEffect(() => {
    fetchStations();
  }, [fetchStations]);

  return { stations, loading, error, toast, fetchStations };
};
